import { useQueryClient } from "@tanstack/react-query";

import {
	getGetOneProvisionedSubscriptionQueryKey,
	getListProvisionedSubscriptionsQueryKey,
	useCancelProvisionedSubscription,
	useCreateProvisionedSubscription,
	useRefundSubscription,
	useRenewSubscription,
	useSetSiteForProvisionedSubscription,
} from "../api/generated/subscription-provisioning/subscription-provisioning";

/**
 * The five provisioning mutations, each keeping the query cache in step with its response.
 *
 * Every mutation resolves to the raw `SubscriptionProvisioningResponseDto`; map it with
 * `toProvisionedSubscription` before rendering it. Variables follow the generated shapes:
 * `create( { data } )`, `renew( { id } )`, `cancel( { id, data: { immediately } } )`, `refund( { id } )`,
 * `setSite( { id, data: { site } } )`.
 *
 * On success the list is invalidated by key prefix — the generated list key is
 * `[ "/api/provisioning/subscriptions", params? ]`, so the bare key matches every page and search — and
 * the detail entry is written directly, so a detail page open on the subscription re-renders without a
 * second round trip.
 *
 * @returns {{create: Object, renew: Object, cancel: Object, refund: Object, setSite: Object}} The
 *          TanStack mutation results.
 */
export default function useProvisionedSubscriptionMutations() {
	const queryClient = useQueryClient();

	const onSuccess = dto => {
		queryClient.setQueryData( getGetOneProvisionedSubscriptionQueryKey( dto.ID ), dto );

		return queryClient.invalidateQueries( { queryKey: getListProvisionedSubscriptionsQueryKey() } );
	};

	const options = { mutation: { onSuccess } };

	return {
		create: useCreateProvisionedSubscription( options ),
		renew: useRenewSubscription( options ),
		cancel: useCancelProvisionedSubscription( options ),
		refund: useRefundSubscription( options ),
		setSite: useSetSiteForProvisionedSubscription( options ),
	};
}
